import { FileTypes, getFileTypeFromExtension } from "@validereinc/utilities";
import type { IconProps } from "./types";

export const FileExtensionsWithIcons = {
  XLS: "xls",
  XLSX: "xlsx",
  DOCX: "docx",
  DOC: "doc",
  CSV: "csv",
  PNG: "png",
  JPEG: "jpeg",
  JPG: "jpg",
  PDF: "pdf",
} as const;
export const FileExtensionsWithIconsSet = new Set(
  Object.values(FileExtensionsWithIcons)
);

const FileExtensionsWithIconsNormalizationMap: Record<
  string,
  Exclude<
    (typeof FileExtensionsWithIcons)[keyof typeof FileExtensionsWithIcons],
    "jpg"
  >
> = {
  jpg: "jpeg",
};

/**
 * Given a file extension, returns a key from the icon mapping file that corresponds to the file extension icon
 * @param extension string representing the file extension without the "."
 * @returns A string representing a key from the icon mapping file.
 */
export const getIconVariantFromFileExtension = (
  extension: string | null
): IconProps["variant"] => {
  if (!extension) return "custom-file";

  const normalizedExtension =
    FileExtensionsWithIconsNormalizationMap[extension] || extension;

  if (FileExtensionsWithIconsSet.has(normalizedExtension)) {
    return `custom-${normalizedExtension}-file`;
  }

  switch (getFileTypeFromExtension(extension)) {
    case FileTypes.WORD_DOCUMENT: {
      return "custom-doc-file";
    }
    case FileTypes.IMAGE: {
      return "file-image";
    }
    case FileTypes.PDF: {
      return "file-pdf";
    }
    case FileTypes.POWERPOINT: {
      return "file-ppt";
    }
    case FileTypes.TEXT_DOCUMENT: {
      return "file-text";
    }
    case FileTypes.CODE_DOCUMENT: {
      return "file-code";
    }
    case FileTypes.CSV: {
      return "file-csv";
    }
    case FileTypes.EXCEL_DOCUMENT: {
      return "file-xls";
    }
    case FileTypes.AUDIO: {
      return "file-audio";
    }
    case FileTypes.VIDEO: {
      return "file-video";
    }
    case FileTypes.OTHER: {
      return "custom-file";
    }
    case FileTypes.UNKNOWN: {
      return "file-x";
    }
    default:
      return "custom-file";
  }
};
